import {YearDay} from './astronomy';
import {skyAt} from './skyColours';
import type {SunYearTurningPoint} from './detailView';

/**
 * The year chart, as arithmetic.
 *
 * Geometry only: two path strings, the positions of everything that gets a tick
 * or a line, and a `fraction` per gradient stop. No element tree, no colours
 * beyond the twilight palette, and nothing that needs a locale — which is why
 * this can be tested without an intl provider, and why the hour labels are
 * formatted by the caller rather than here.
 *
 * Shared because it is the part that was expensive to get right, not because it
 * is long. The gridline step in particular is derived from the pixels available
 * rather than from the range, and the reason is written out below; a webapp
 * chart at a different height would otherwise rediscover the same collision.
 *
 * The colour ramp under the curve is deliberately NOT here. Mobile's is a
 * `dayCurveLow/Mid/High` theme token triple; the webapp will have its own. Each
 * stop carries `fraction` — where the day sits between nothing and 24 hours —
 * and each app mixes its own ramp from it. Theme colours do not cross the seam
 * in either direction.
 */

const SECONDS_PER_DAY = 86400;

/** One gradient stop per week. 365 stops is a lot of nodes for a smooth ramp. */
const GRADIENT_STEP = 7;

/** Headroom above the tallest possible curve, so 24 h does not touch the frame. */
const TOP_PADDING = 8;

/**
 * Minimum pixels between gridlines, which is what sets the hour step:
 * `ceil(minGridGap / pixelsPerHour)`.
 *
 * The y scale is the whole 0-24 h — that is what makes a polar zero mean
 * something — which leaves a temperate location's 5-6 h band occupying about a
 * quarter of the plot. One-hour steps there are four pixels apart, and the
 * built app printed "14 hr", "12 hr" and "12 hr" on top of one another.
 *
 * 18 rather than 14 because mobile's plot is 136dp. At 14 a taller plot derives
 * a *three*-hour step, which costs twice: a temperate year is labelled
 * 9h/12h/15h instead of the round 8h/12h/16h, and the extra gridline lands near
 * the annual minimum, where the December curve then runs straight through it.
 */
const MIN_GRID_GAP = 18;
const MAX_GRIDLINES = 3;

/** A one-day polar run still has to be visible. */
const MIN_PLATEAU_WIDTH = 2;

export type SunYearPlateau = {
  /** Stable key: the regime and the run's first index. */
  key: string;
  regime: 'midnightSun' | 'polarNight';
  x: number;
  width: number;
  /** The twilight palette — the seam's one sanctioned colour. */
  colour: string;
};

export type SunYearGridLine = {
  /** Whole hours of daylight this line stands for; the caller formats it. */
  hours: number;
  y: number;
};

export type SunYearMonthMark = {
  /** 0-11. January's separator is the frame edge and is normally not drawn. */
  month: number;
  index: number;
  x: number;
};

export type SunYearStop = {
  date: string;
  /** 0-1 across the plot. */
  offset: number;
  /** 0-1 of a full day, for the caller's own colour ramp. */
  fraction: number;
};

export type SunYearChartGeometry = {
  /** The stroked curve. */
  line: string;
  /** The same curve closed back to the baseline. */
  area: string;
  gridLines: SunYearGridLine[];
  months: SunYearMonthMark[];
  plateaus: SunYearPlateau[];
  stops: SunYearStop[];
  /** `null` when `todayIso` is not one of `days`. */
  today: {index: number; x: number; y: number; fraction: number} | null;
  /** Only the turning points that fall inside `days`, positioned. */
  turningPoints: {key: string; label: string; date: string; x: number}[];
};

export type SunYearChartParams = {
  days: YearDay[];
  /** `YYYY-MM-DD` in the location's own zone. Absent from `days` is fine. */
  todayIso: string;
  turningPoints: SunYearTurningPoint[];
  /** Measured by the caller. Zero until a layout pass has happened. */
  width: number;
  height: number;
  gradientStep?: number;
  minGridGap?: number;
  maxGridLines?: number;
  topPadding?: number;
};

/** Where a day sits between nothing and 24 hours. */
export const dayLightFraction = (seconds: number) =>
  Math.max(0, Math.min(1, seconds / SECONDS_PER_DAY));

/** Contiguous index runs of a regime. Two for a polar night, which straddles 31 Dec. */
const runsOf = (days: YearDay[], regime: YearDay['regime']) => {
  const runs: {from: number; to: number}[] = [];
  days.forEach((day, index) => {
    const last = runs[runs.length - 1];
    if (day.regime !== regime) {
      return;
    }
    if (last !== undefined && last.to === index - 1) {
      last.to = index;
    } else {
      runs.push({from: index, to: index});
    }
  });
  return runs;
};

export const yearChartGeometry = ({
  days,
  todayIso,
  turningPoints,
  width,
  height,
  gradientStep = GRADIENT_STEP,
  minGridGap = MIN_GRID_GAP,
  maxGridLines = MAX_GRIDLINES,
  topPadding = TOP_PADDING,
}: SunYearChartParams): SunYearChartGeometry | null => {
  if (width <= 0 || height <= 0 || days.length < 2) {
    return null;
  }

  const x = (index: number) => (index / (days.length - 1)) * width;
  const y = (seconds: number) =>
    height - (seconds / SECONDS_PER_DAY) * (height - topPadding);

  const line = days
    .map(
      (day, index) =>
        `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(day.seconds).toFixed(1)}`,
    )
    .join('');

  /*
   * Multiples of the derived step that fall inside the range, widened to the
   * nearest ones outside it when the range is too narrow to contain two — a
   * single gridline is not an axis.
   */
  const values = days.map((day) => day.seconds);
  const low = Math.min(...values) / 3600;
  const high = Math.max(...values) / 3600;
  const pixelsPerHour = (height - topPadding) / 24;
  const step = Math.max(1, Math.ceil(minGridGap / pixelsPerHour));

  const multiples = [];
  for (let hours = step; hours < 24; hours += step) {
    multiples.push(hours);
  }
  let gridHours = multiples.filter((hours) => hours >= low && hours <= high);
  if (gridHours.length < 2) {
    const below = multiples.filter((hours) => hours < low).pop();
    const above = multiples.find((hours) => hours > high);
    gridHours = [below, ...gridHours, above].filter(
      (hours): hours is number => hours !== undefined,
    );
  }
  if (gridHours.length > maxGridLines) {
    const stride = Math.ceil(gridHours.length / maxGridLines);
    gridHours = gridHours.filter((_, index) => index % stride === 0);
  }

  const months = days.reduce<SunYearMonthMark[]>((starts, day, index) => {
    if (day.date.slice(8, 10) === '01') {
      starts.push({
        index,
        x: x(index),
        month: Number(day.date.slice(5, 7)) - 1,
      });
    }
    return starts;
  }, []);

  const plateaus = (['midnightSun', 'polarNight'] as const).flatMap((regime) =>
    runsOf(days, regime).map((run) => ({
      key: `${regime}-${run.from}`,
      regime,
      x: x(run.from),
      width: Math.max(x(run.to) - x(run.from), MIN_PLATEAU_WIDTH),
      // The plateaus are the data at a polar latitude: 68 days that never end
      // and 48 that never begin.
      colour: skyAt(regime === 'midnightSun' ? 0 : -30),
    })),
  );

  /*
   * Built as a list, not filtered inside a render loop.
   *
   * `react-native-svg`'s `LinearGradient` throws "The argument must be a React
   * element, but you passed null" on a null child, and the pane goes to the
   * error boundary. Mapping over all 365 days and returning null between stops
   * did precisely that — invisible to the tests, because the shared svg mock
   * renders every primitive as a plain View.
   *
   * Indexed off the loop rather than looked up by date: a `findIndex` per stop
   * is 53 scans of 365 days.
   */
  const stops = days.reduce<SunYearStop[]>((list, day, index) => {
    if (index % gradientStep === 0 || index === days.length - 1) {
      list.push({
        date: day.date,
        offset: index / (days.length - 1),
        fraction: dayLightFraction(day.seconds),
      });
    }
    return list;
  }, []);

  const todayIndex = days.findIndex((day) => day.date === todayIso);
  const today =
    todayIndex === -1
      ? null
      : {
          index: todayIndex,
          x: x(todayIndex),
          y: y(days[todayIndex].seconds),
          fraction: dayLightFraction(days[todayIndex].seconds),
        };

  return {
    line,
    area: `${line}L${width},${height}L0,${height}Z`,
    gridLines: gridHours.map((hours) => ({hours, y: y(hours * 3600)})),
    months,
    plateaus,
    stops,
    today,
    turningPoints: turningPoints
      .map((point) => ({
        ...point,
        key: `${point.label}-${point.date}`,
        index: days.findIndex((day) => day.date === point.date),
      }))
      .filter((point) => point.index !== -1)
      .map(({key, label, date, index}) => ({key, label, date, x: x(index)})),
  };
};
